import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';

import normalizeEmail from 'utils/normalizeEmail';
import checkIsValidEmail from 'utils/checkIsValidEmail';
import normalizeCif from 'utils/normalizeCif';
import cleanText from 'utils/cleanText';
import { generateEmail } from 'utils/filters';
import { addPartner } from 'modules/partners/redux/partnerActions';
import { checkPartnerExists } from 'modules/partners/api/checkPartnerExists';
import infoSwal from 'utils/infoSwal';
import { addPartnerModalInitValue } from '../constants/addPartnerModalInitValue';
import { AddPartnerModalContextType } from '../types/AddPartnerModalContextType';

const AddPartnerModalContext = createContext<AddPartnerModalContextType>(
  addPartnerModalInitValue
);

export const AddPartnerModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [name, setName] = useState<string>('');
  const [cif, setCif] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [naturalPerson, setNaturalPerson] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const onChangeName = (value: string) => {
    setName(value);
  };

  const onChangeCif = (value: string) => {
    const normalicedCif = normalizeCif(value);
    setCif(normalicedCif);
  };

  const onChangeEmail = (value: string) => {
    const normalicedEmail = normalizeEmail(value);
    setEmail(normalicedEmail);

    const isEmailValid = checkIsValidEmail(normalicedEmail);
    setIsEmailValid(isEmailValid);

    setEmail(value);
  };

  const handleChangeCif = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    onChangeCif(event.target.value);
  };

  const handleChangeEmail = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    onChangeEmail(event.target.value);
  };

  const handleChangeNaturalPerson = () => {
    setNaturalPerson((prev) => !prev);
  };

  const handleSavePartner = async () => {
    try {
      setIsLoading(true);

      const partnerData = {
        name: cleanText(name),
        email: email ? cleanText(email) : generateEmail(actualSociety),
        date: new Date(),
        society: actualSociety._id,
        societyName: actualSociety.name,
        userEmail: user.email,
        cif: cleanText(cif),
        user: user['_id'],
        isNaturalPerson: naturalPerson,
      };

      const partnerExists = await store.dispatch(
        checkPartnerExists(actualSociety?.['_id'], cif)
      );

      if (partnerExists) {
        infoSwal(t('PartnerWithThaCifAlreadyExists'));
        setIsLoading(false);
        return;
      }

      await store.dispatch(addPartner(partnerData));
      setIsLoading(false);

      successSwal(t('PartnerAddedSuccessfully'));
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorAddingPartner'));
      handleCloseModal();
    }
  };

  return (
    <AddPartnerModalContext.Provider
      value={{
        data: {
          name,
          cif,
          email,
          isEmailValid,
          naturalPerson,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeCif,
          handleChangeEmail,
          onChangeName,
          onChangeCif,
          onChangeEmail,
          handleChangeNaturalPerson,
          handleSavePartner,
        },
      }}
    >
      {children}
    </AddPartnerModalContext.Provider>
  );
};

export const useAddPartnerModal = () => {
  const context = useContext(AddPartnerModalContext);
  if (!context) {
    throw new Error(
      'useAddPartnerModal must be used within a AddPartnerModalProvider'
    );
  }
  return context;
};
